<template>
  <Teleport to="body">
    <div v-if="mostrar" class="modal-overlay" @click.self="fecharModal">
      <div :class="['modal-content', { 'modal-fechando': animandoFechamento }]">
        <div class="modal-header">
          <h2>{{ modoEdicao ? 'Editar Template' : 'Novo Template' }}</h2>
          <button class="btn-close" @click="fecharModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Modelos Prontos (somente ao criar) -->
          <div v-if="!modoEdicao" class="form-group">
            <label>Usar modelo pronto</label>
            <div class="modelo-selector">
              <select v-model="categoriaFiltro" class="form-input modelo-filtro">
                <option value="">Todas as categorias</option>
                <option v-for="cat in categoriasDisponiveis" :key="cat" :value="cat">{{ cat }}</option>
              </select>
              <select v-model="modeloSelecionado" class="form-input" @change="aplicarModelo">
                <option value="">-- Selecione um modelo (opcional) --</option>
                <option
                  v-for="(modelo, idx) in modelosFiltrados"
                  :key="idx"
                  :value="idx"
                >
                  {{ modelo.canal === 'email' ? '[Email]' : modelo.canal === 'whatsapp' ? '[WhatsApp]' : '[Ambos]' }}
                  {{ modelo.usar_ia ? '[IA]' : '' }}
                  {{ modelo.nome }}
                </option>
              </select>
            </div>
          </div>

          <!-- Nome do Template -->
          <div class="form-group">
            <label>Nome do Template *</label>
            <input
              v-model="form.nome"
              type="text"
              class="form-input"
              placeholder="Ex: Boas-vindas novos pacientes"
              maxlength="100"
            />
          </div>

          <!-- Canal -->
          <div class="form-group">
            <label>Canal *</label>
            <div class="canal-options">
              <label class="canal-option" :class="{ selected: form.canal === 'email' }">
                <input v-model="form.canal" type="radio" value="email" />
                <i class="fas fa-envelope"></i>
                <span>Email</span>
              </label>
              <label class="canal-option" :class="{ selected: form.canal === 'whatsapp' }">
                <input v-model="form.canal" type="radio" value="whatsapp" />
                <i class="fab fa-whatsapp"></i>
                <span>WhatsApp</span>
              </label>
              <label class="canal-option" :class="{ selected: form.canal === 'ambos' }">
                <input v-model="form.canal" type="radio" value="ambos" />
                <i class="fas fa-bullhorn"></i>
                <span>Ambos</span>
              </label>
            </div>
          </div>

          <!-- Assunto (somente para email) -->
          <div v-if="form.canal === 'email' || form.canal === 'ambos'" class="form-group">
            <label>Assunto do Email</label>
            <input
              v-model="form.assunto"
              type="text"
              class="form-input"
              placeholder="Ex: Dicas de nutrição para você!"
              maxlength="200"
            />
          </div>

          <!-- Toggle Usar IA -->
          <div class="form-group">
            <label class="toggle-label">
              <input v-model="form.usar_ia" type="checkbox" class="toggle-input" />
              <span class="toggle-slider"></span>
              <span class="toggle-text">
                <i class="fas fa-robot"></i>
                Usar IA para gerar conteúdo personalizado
              </span>
            </label>
          </div>

          <!-- Modo Texto Fixo -->
          <template v-if="!form.usar_ia">
            <div class="form-group">
              <label>Conteúdo da Mensagem *</label>
              <div class="editor-wrapper">
                <!-- Abas Visual / Código HTML -->
                <div class="editor-tabs">
                  <button
                    type="button"
                    :class="['editor-tab', { active: editorTab === 'visual' }]"
                    @click="trocarAba('visual')"
                  >
                    <i class="fas fa-eye"></i> Visual
                  </button>
                  <button
                    type="button"
                    :class="['editor-tab', { active: editorTab === 'html' }]"
                    @click="trocarAba('html')"
                  >
                    <i class="fas fa-code"></i> Código HTML
                  </button>
                </div>

                <!-- Toolbar (visível em ambas as abas) -->
                <div class="editor-toolbar">
                  <!-- Formatação (somente aba Visual) -->
                  <template v-if="editorTab === 'visual'">
                    <div class="toolbar-group">
                      <select
                        class="fmt-select"
                        title="Tamanho do texto"
                        @mousedown="salvarSelecao"
                        @change="aplicarBloco($event)"
                      >
                        <option value="" disabled selected>Formato</option>
                        <option value="p">Normal</option>
                        <option value="h2">Título</option>
                        <option value="h3">Subtítulo</option>
                        <option value="h4">Pequeno</option>
                      </select>
                    </div>
                    <div class="toolbar-divider"></div>
                    <div class="toolbar-group">
                      <button type="button" class="fmt-btn" title="Negrito" @mousedown.prevent="execCmd('bold')">
                        <i class="fas fa-bold"></i>
                      </button>
                      <button type="button" class="fmt-btn" title="Itálico" @mousedown.prevent="execCmd('italic')">
                        <i class="fas fa-italic"></i>
                      </button>
                      <button type="button" class="fmt-btn" title="Sublinhado" @mousedown.prevent="execCmd('underline')">
                        <i class="fas fa-underline"></i>
                      </button>
                    </div>
                    <div class="toolbar-divider"></div>
                    <div class="toolbar-group">
                      <button type="button" class="fmt-btn" title="Lista" @mousedown.prevent="execCmd('insertUnorderedList')">
                        <i class="fas fa-list-ul"></i>
                      </button>
                      <button type="button" class="fmt-btn" title="Lista numerada" @mousedown.prevent="execCmd('insertOrderedList')">
                        <i class="fas fa-list-ol"></i>
                      </button>
                    </div>
                    <div class="toolbar-divider"></div>
                    <div class="toolbar-group">
                      <button type="button" class="fmt-btn" title="Inserir link" @mousedown.prevent="inserirLink">
                        <i class="fas fa-link"></i>
                      </button>
                      <button type="button" class="fmt-btn" title="Remover link" @mousedown.prevent="execCmd('unlink')">
                        <i class="fas fa-unlink"></i>
                      </button>
                      <button type="button" class="fmt-btn" title="Inserir imagem" @mousedown.prevent="abrirSeletorImagem">
                        <i class="fas fa-image"></i>
                      </button>
                    </div>
                    <div class="toolbar-divider"></div>
                  </template>
                  <div class="toolbar-group toolbar-vars">
                    <span class="toolbar-label">Variáveis:</span>
                    <button
                      v-for="(desc, varName) in variaveisDisponiveis"
                      :key="varName"
                      type="button"
                      class="var-btn"
                      :title="desc"
                      @mousedown.prevent="inserirVariavel(varName)"
                    >
                      {{ varName }}
                    </button>
                  </div>
                </div>

                <!-- Aba Visual (contenteditable) -->
                <div
                  v-show="editorTab === 'visual'"
                  ref="editorVisual"
                  class="editor-visual"
                  contenteditable="true"
                  @input="onVisualInput"
                  @paste="onVisualPaste"
                  @focus="onVisualFocus"
                ></div>

                <!-- Aba Código HTML -->
                <textarea
                  v-show="editorTab === 'html'"
                  ref="textareaCorpo"
                  v-model="form.corpo"
                  class="form-textarea editor-html-textarea"
                  placeholder="Digite o código HTML aqui..."
                  rows="10"
                ></textarea>
              </div>
            </div>
          </template>

          <!-- Modo IA -->
          <template v-else>
            <div class="form-group">
              <label>Prompt para a IA *</label>
              <textarea
                v-model="form.prompt_ia"
                class="form-textarea"
                placeholder="Escreva as instruções para a IA gerar o conteúdo...

Ex: Escreva uma mensagem de boas-vindas calorosa para o paciente {primeiro_nome}, mencionando que estamos felizes em tê-lo(a) como paciente."
                rows="6"
              ></textarea>
            </div>

          </template>

          <!-- Toggle CTA -->
          <div class="form-group">
            <label class="toggle-label">
              <input v-model="form.usar_cta" type="checkbox" class="toggle-input" />
              <span class="toggle-slider"></span>
              <span class="toggle-text">
                <i class="fas fa-link"></i>
                Incluir link de ação (CTA)
              </span>
            </label>
          </div>

          <!-- Campos CTA -->
          <div v-if="form.usar_cta" class="cta-fields">
            <div class="form-group">
              <label>Texto do botão *</label>
              <input
                v-model="form.cta_texto"
                type="text"
                class="form-input"
                placeholder="Ex: Agendar consulta, Saiba mais, Acessar portal"
                maxlength="100"
              />
            </div>
            <div class="form-group">
              <label>URL do link *</label>
              <input
                v-model="form.cta_url"
                type="url"
                class="form-input"
                placeholder="https://exemplo.com/agendar"
                maxlength="500"
              />
            </div>
            <div class="cta-preview">
              <span class="cta-preview-label">Preview:</span>
              <template v-if="form.canal === 'whatsapp'">
                <span class="cta-preview-whatsapp">
                  {{ form.cta_texto || 'Texto do botão' }}: {{ form.cta_url || 'https://...' }}
                </span>
              </template>
              <template v-else>
                <a
                  class="cta-preview-btn"
                  :href="form.cta_url || '#'"
                  target="_blank"
                  @click.prevent
                >
                  {{ form.cta_texto || 'Texto do botão' }}
                </a>
              </template>
            </div>
          </div>

          <!-- Anexos / Mídias -->
          <div class="form-group">
            <label>Anexos / Mídias</label>
            <div class="medias-section">
              <!-- Mídias selecionadas -->
              <div v-if="form.media_ids.length" class="medias-selecionadas">
                <div
                  v-for="media in mediasSelecionadasInfo"
                  :key="media.id"
                  class="media-chip"
                >
                  <i :class="getMediaIcon(media)"></i>
                  <span class="media-chip-nome">{{ media.nome }}</span>
                  <span class="media-chip-size">{{ media.tamanho_formatado }}</span>
                  <button type="button" class="media-chip-remove" @click="removerMedia(media.id)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
              <button type="button" class="btn-add-media" @click="mostrarSeletorMidias = true">
                <i class="fas fa-paperclip"></i>
                Adicionar Mídia
              </button>
            </div>
          </div>

          <!-- Modal Seletor de Mídias -->
          <Teleport to="body">
            <div v-if="mostrarSeletorMidias" class="media-selector-overlay" @click.self="mostrarSeletorMidias = false">
              <div class="media-selector-modal">
                <div class="media-selector-header">
                  <h3>Selecionar Mídias</h3>
                  <button class="btn-close" @click="mostrarSeletorMidias = false">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div class="media-selector-body">
                  <div v-if="carregandoMidias" class="media-selector-loading">
                    <div class="spinner-small"></div>
                    Carregando...
                  </div>
                  <div v-else-if="!midiasDisponiveis.length" class="media-selector-empty">
                    Nenhuma mídia disponível. Faça upload na aba Mídias.
                  </div>
                  <div v-else class="media-selector-grid">
                    <div
                      v-for="media in midiasDisponiveis"
                      :key="media.id"
                      class="media-selector-item"
                      :class="{ selected: form.media_ids.includes(media.id) }"
                      @click="toggleMedia(media.id)"
                    >
                      <div class="media-selector-thumb">
                        <img v-if="media.tipo === 'imagem'" :src="media.url" :alt="media.nome" />
                        <i v-else :class="getMediaIcon(media)"></i>
                      </div>
                      <span class="media-selector-nome">{{ media.nome }}</span>
                      <span class="media-selector-size">{{ media.tamanho_formatado }}</span>
                      <div v-if="form.media_ids.includes(media.id)" class="media-selector-check">
                        <i class="fas fa-check-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="media-selector-footer">
                  <span>{{ form.media_ids.length }} selecionada(s)</span>
                  <button class="btn-confirmar-midias" @click="mostrarSeletorMidias = false">
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </Teleport>

          <!-- Modal Seletor de Imagens Inline -->
          <Teleport to="body">
            <div v-if="mostrarSeletorImagens" class="media-selector-overlay" @click.self="mostrarSeletorImagens = false">
              <div class="media-selector-modal">
                <div class="media-selector-header">
                  <h3>Inserir Imagem no Conteúdo</h3>
                  <button class="btn-close" @click="mostrarSeletorImagens = false">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div class="media-selector-body">
                  <div v-if="carregandoMidias" class="media-selector-loading">
                    <div class="spinner-small"></div>
                    Carregando...
                  </div>
                  <div v-else-if="!imagensDisponiveis.length" class="media-selector-empty">
                    Nenhuma imagem disponível. Faça upload na aba Mídias.
                  </div>
                  <div v-else class="media-selector-grid">
                    <div
                      v-for="media in imagensDisponiveis"
                      :key="media.id"
                      class="media-selector-item media-inline-item"
                      @click="inserirImagemInline(media)"
                    >
                      <div class="media-selector-thumb">
                        <img :src="media.url" :alt="media.nome" />
                      </div>
                      <span class="media-selector-nome">{{ media.nome }}</span>
                      <span class="media-selector-size">{{ media.tamanho_formatado }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Teleport>

          <!-- Status Ativo -->
          <div class="form-group">
            <label class="toggle-label">
              <input v-model="form.ativo" type="checkbox" class="toggle-input" />
              <span class="toggle-slider"></span>
              <span class="toggle-text">Template ativo</span>
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancelar" @click="fecharModal">
            Cancelar
          </button>
          <button type="button" class="btn-salvar" :disabled="loading" @click="salvar">
            <span v-if="loading" class="spinner-btn"></span>
            {{ modoEdicao ? 'Salvar Alterações' : 'Criar Template' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import { templateModels, getCategorias } from '@/data/templateModels'

const props = defineProps({
  mostrar: { type: Boolean, default: false },
  modoEdicao: { type: Boolean, default: false },
  template: { type: Object, default: null },
  variaveisDisponiveis: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['fechar', 'salvar'])

const textareaCorpo = ref(null)
const editorVisual = ref(null)
const editorTab = ref('visual')
const animandoFechamento = ref(false)
const sincronizando = ref(false)

// Modelos prontos
const categoriaFiltro = ref('')
const modeloSelecionado = ref('')

const categoriasDisponiveis = computed(() => getCategorias())

const modelosFiltrados = computed(() => {
  if (!categoriaFiltro.value) return templateModels
  return templateModels.filter((t) => t.categoria === categoriaFiltro.value)
})

function aplicarModelo() {
  const idx = modeloSelecionado.value
  if (idx === '' || idx === undefined) return

  const modelo = modelosFiltrados.value[idx]
  if (!modelo) return

  form.value.nome = modelo.nome || ''
  form.value.canal = modelo.canal || 'email'
  form.value.assunto = modelo.assunto || ''
  form.value.corpo = modelo.corpo || ''
  form.value.usar_ia = modelo.usar_ia || false
  form.value.prompt_ia = modelo.prompt_ia || ''
  form.value.modelo_ia = modelo.modelo_ia || 'gpt-4.1-mini'
  form.value.temperatura = modelo.temperatura ?? 0.8
  form.value.usar_cta = modelo.usar_cta || false
  form.value.cta_texto = modelo.cta_texto || ''
  form.value.cta_url = modelo.cta_url || ''

  modeloSelecionado.value = ''
}

// === Mídias ===
const mostrarSeletorMidias = ref(false)
const midiasDisponiveis = ref([])
const carregandoMidias = ref(false)

const mediasSelecionadasInfo = computed(() => {
  return form.value.media_ids
    .map(id => midiasDisponiveis.value.find(m => m.id === id))
    .filter(Boolean)
})

function getMediaIcon(media) {
  if (media.tipo === 'imagem') return 'fas fa-image'
  if (media.mime_type === 'application/pdf') return 'fas fa-file-pdf'
  if (media.mime_type?.includes('word') || media.mime_type?.includes('.doc')) return 'fas fa-file-word'
  if (media.mime_type?.includes('sheet') || media.mime_type?.includes('.xls') || media.mime_type?.includes('excel')) return 'fas fa-file-excel'
  return 'fas fa-file'
}

function toggleMedia(id) {
  const idx = form.value.media_ids.indexOf(id)
  if (idx >= 0) {
    form.value.media_ids.splice(idx, 1)
  } else {
    form.value.media_ids.push(id)
  }
}

function removerMedia(id) {
  form.value.media_ids = form.value.media_ids.filter(mid => mid !== id)
}

// === Inserir Imagem Inline ===
const mostrarSeletorImagens = ref(false)

const imagensDisponiveis = computed(() => {
  return midiasDisponiveis.value.filter(m => m.tipo === 'imagem')
})

function abrirSeletorImagem() {
  salvarSelecao()
  if (!midiasDisponiveis.value.length) {
    carregarMidiasDisponiveis()
  }
  mostrarSeletorImagens.value = true
}

function inserirImagemInline(media) {
  mostrarSeletorImagens.value = false
  nextTick(() => {
    garantirFocoEditor()
    const imgHtml = `<img src="${media.url}" alt="${media.nome}" class="img-inline" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />`
    document.execCommand('insertHTML', false, imgHtml)
    nextTick(() => {
      form.value.corpo = editorVisual.value?.innerHTML || ''
      salvarSelecao()
    })
  })
}

async function carregarMidiasDisponiveis() {
  carregandoMidias.value = true
  try {
    const { data } = await axios.get('/api/notificacoes/midias')
    midiasDisponiveis.value = data
  } catch {
    // silencioso
  } finally {
    carregandoMidias.value = false
  }
}

const formPadrao = () => ({
  nome: '',
  canal: 'email',
  assunto: '',
  corpo: '',
  variaveis: [],
  usar_ia: false,
  prompt_ia: '',
  modelo_ia: 'gpt-4.1-mini',
  temperatura: 0.8,
  usar_cta: false,
  cta_texto: '',
  cta_url: '',
  ativo: true,
  media_ids: [],
})

const form = ref(formPadrao())

// Watch para preencher o form quando abrir para edição
watch(
  () => props.mostrar,
  (aberto) => {
    if (aberto) {
      categoriaFiltro.value = ''
      modeloSelecionado.value = ''
      if (props.modoEdicao && props.template) {
        form.value = {
          ...formPadrao(),
          ...props.template,
          media_ids: props.template.medias ? props.template.medias.map(m => m.id) : [],
        }
      } else {
        form.value = formPadrao()
      }
      carregarMidiasDisponiveis()
    }
  },
  { immediate: true }
)


// --- Editor Visual/HTML ---

// Limpar HTML: normalizar blocos, converter <div> para <p>, envolver texto solto
function limparHtml(html) {
  if (!html) return ''

  const temp = document.createElement('div')
  temp.innerHTML = html

  // 1. Converter <div> para <p>
  temp.querySelectorAll('div').forEach((div) => {
    const p = document.createElement('p')
    p.innerHTML = div.innerHTML
    div.replaceWith(p)
  })

  // 2. Remover spans vazios (sem atributos úteis)
  temp.querySelectorAll('span').forEach((span) => {
    if (!span.getAttribute('style') && !span.getAttribute('id')) {
      span.replaceWith(...span.childNodes)
    }
  })

  // 3. Remover style/class de elementos que não são links nem imagens inline
  temp.querySelectorAll('[style]:not(a):not(img.img-inline)').forEach((el) => el.removeAttribute('style'))
  temp.querySelectorAll('[class]:not(img.img-inline)').forEach((el) => el.removeAttribute('class'))

  // 4. Envolver nós soltos (texto/inline) no topo em <p>
  const blockTags = new Set([
    'P', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'BLOCKQUOTE', 'TABLE', 'HR', 'PRE',
  ])
  const nodes = [...temp.childNodes]
  let bareGroup = []

  function flushBare(beforeNode) {
    if (!bareGroup.length) return
    const hasContent = bareGroup.some((n) =>
      n.nodeType === 3 ? n.textContent.trim() !== '' : true,
    )
    if (hasContent) {
      const p = document.createElement('p')
      bareGroup.forEach((n) => p.appendChild(n))
      // Remover <br> final dentro do <p> criado
      const lastChild = p.lastChild
      if (lastChild && lastChild.nodeType === 1 && lastChild.tagName === 'BR') {
        lastChild.remove()
      }
      if (beforeNode) {
        temp.insertBefore(p, beforeNode)
      } else {
        temp.appendChild(p)
      }
    }
    bareGroup = []
  }

  nodes.forEach((node) => {
    const isBlock = node.nodeType === 1 && blockTags.has(node.tagName)
    if (isBlock) {
      flushBare(node)
    } else {
      bareGroup.push(node)
    }
  })
  flushBare(null)

  // 5. Remover <p> totalmente vazios (sem texto, sem <br>, sem <img>)
  temp.querySelectorAll('p').forEach((p) => {
    if (!p.textContent.trim() && !p.querySelector('br, img')) {
      p.remove()
    }
  })

  return temp.innerHTML.trim()
}

function inicializarEditor() {
  if (!editorVisual.value) return
  // Forçar <p> como bloco padrão em vez de <div>
  document.execCommand('defaultParagraphSeparator', false, 'p')
  editorVisual.value.innerHTML = form.value.corpo || ''
}

function trocarAba(aba) {
  if (aba === editorTab.value) return

  if (aba === 'visual') {
    editorTab.value = 'visual'
    nextTick(() => inicializarEditor())
  } else {
    // Visual -> HTML: limpar e atualizar
    if (editorVisual.value) {
      form.value.corpo = limparHtml(editorVisual.value.innerHTML)
    }
    editorTab.value = 'html'
  }
}

// --- Gerenciar seleção do editor ---
let selecaoSalva = null

function salvarSelecao() {
  const sel = window.getSelection()
  if (sel.rangeCount > 0 && editorVisual.value?.contains(sel.anchorNode)) {
    selecaoSalva = sel.getRangeAt(0).cloneRange()
  }
}

function restaurarSelecao() {
  if (!selecaoSalva) return false
  const sel = window.getSelection()
  sel.removeAllRanges()
  sel.addRange(selecaoSalva)
  return true
}

function garantirFocoEditor() {
  if (!editorVisual.value) return
  const sel = window.getSelection()
  // Se a seleção já está dentro do editor, ok
  if (sel.rangeCount > 0 && editorVisual.value.contains(sel.anchorNode)) return
  // Tentar restaurar seleção salva
  if (restaurarSelecao()) return
  // Última opção: colocar cursor no final
  editorVisual.value.focus()
  const range = document.createRange()
  range.selectNodeContents(editorVisual.value)
  range.collapse(false)
  sel.removeAllRanges()
  sel.addRange(range)
}

function onVisualFocus() {
  document.execCommand('defaultParagraphSeparator', false, 'p')
}

function onVisualInput() {
  if (sincronizando.value) return
  sincronizando.value = true
  form.value.corpo = editorVisual.value?.innerHTML || ''
  sincronizando.value = false
  // Salvar seleção atual para uso futuro
  salvarSelecao()
}

function onVisualPaste(e) {
  e.preventDefault()
  const html = e.clipboardData.getData('text/html')
  const text = e.clipboardData.getData('text/plain')

  if (html) {
    const temp = document.createElement('div')
    temp.innerHTML = html
    temp.querySelectorAll('[style]').forEach((el) => {
      if (el.tagName !== 'A' && !(el.tagName === 'IMG' && el.classList.contains('img-inline'))) el.removeAttribute('style')
    })
    temp.querySelectorAll('[class]:not(img.img-inline)').forEach((el) => el.removeAttribute('class'))
    temp.querySelectorAll('meta, link, style, script').forEach((el) => el.remove())
    document.execCommand('insertHTML', false, temp.innerHTML)
  } else {
    document.execCommand('insertText', false, text)
  }
  onVisualInput()
}

function execCmd(command, value = null) {
  if (!editorVisual.value) return
  garantirFocoEditor()
  document.execCommand(command, false, value)
  nextTick(() => {
    form.value.corpo = editorVisual.value?.innerHTML || ''
    salvarSelecao()
  })
}

function aplicarBloco(event) {
  const tag = event.target.value
  if (!tag) return
  // Restaurar seleção antes de aplicar (select rouba foco)
  garantirFocoEditor()
  document.execCommand('formatBlock', false, tag)
  // Resetar o select para mostrar "Formato" novamente
  event.target.selectedIndex = 0
  nextTick(() => {
    form.value.corpo = editorVisual.value?.innerHTML || ''
    salvarSelecao()
    editorVisual.value?.focus()
  })
}

function inserirLink() {
  // Salvar seleção antes do prompt (que rouba foco)
  salvarSelecao()
  const url = prompt('URL do link:', 'https://')
  if (url) {
    // Restaurar seleção e aplicar o link
    restaurarSelecao()
    document.execCommand('createLink', false, url)
    nextTick(() => {
      form.value.corpo = editorVisual.value?.innerHTML || ''
      salvarSelecao()
    })
  }
}

// Sincronizar corpo -> visual quando o form muda externamente (ex: aplicar modelo)
watch(
  () => form.value.corpo,
  (newVal) => {
    if (sincronizando.value) return
    if (editorTab.value === 'visual' && editorVisual.value) {
      if (editorVisual.value.innerHTML !== newVal) {
        sincronizando.value = true
        editorVisual.value.innerHTML = newVal || ''
        sincronizando.value = false
      }
    }
  }
)

// Ao abrir o modal, inicializar o editor visual
watch(
  () => props.mostrar,
  (aberto) => {
    if (aberto) {
      selecaoSalva = null
      editorTab.value = 'visual'
      nextTick(() => inicializarEditor())
    }
  }
)

function inserirVariavel(varName) {
  if (editorTab.value === 'visual') {
    if (!editorVisual.value) return
    garantirFocoEditor()
    document.execCommand('insertText', false, varName)
    nextTick(() => {
      form.value.corpo = editorVisual.value?.innerHTML || ''
      salvarSelecao()
    })
    return
  }

  // Inserir no textarea (aba HTML)
  if (!textareaCorpo.value) return

  const textarea = textareaCorpo.value
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = form.value.corpo

  form.value.corpo = text.substring(0, start) + varName + text.substring(end)

  nextTick(() => {
    textarea.focus()
    textarea.setSelectionRange(start + varName.length, start + varName.length)
  })
}

function validarForm() {
  if (!form.value.nome?.trim()) {
    Swal.fire('Atenção', 'Nome do template é obrigatório.', 'warning')
    return false
  }

  if (!form.value.canal) {
    Swal.fire('Atenção', 'Selecione um canal.', 'warning')
    return false
  }

  if (form.value.usar_ia) {
    if (!form.value.prompt_ia?.trim()) {
      Swal.fire('Atenção', 'O prompt para IA é obrigatório.', 'warning')
      return false
    }
  } else {
    if (!form.value.corpo?.trim()) {
      Swal.fire('Atenção', 'O conteúdo da mensagem é obrigatório.', 'warning')
      return false
    }
  }

  if (form.value.usar_cta) {
    if (!form.value.cta_texto?.trim()) {
      Swal.fire('Atenção', 'O texto do botão CTA é obrigatório.', 'warning')
      return false
    }
    if (!form.value.cta_url?.trim()) {
      Swal.fire('Atenção', 'A URL do CTA é obrigatória.', 'warning')
      return false
    }
    try {
      new URL(form.value.cta_url)
    } catch {
      Swal.fire('Atenção', 'A URL do CTA deve ser válida (ex: https://exemplo.com).', 'warning')
      return false
    }
  }

  return true
}

function salvar() {
  if (!validarForm()) return

  const dados = { ...form.value }

  // Limpar HTML do corpo antes de salvar
  if (!dados.usar_ia && dados.corpo) {
    // Se estava na aba visual, sincronizar e limpar
    if (editorTab.value === 'visual' && editorVisual.value) {
      dados.corpo = limparHtml(editorVisual.value.innerHTML)
    } else {
      dados.corpo = limparHtml(dados.corpo)
    }
  }

  emit('salvar', dados)
}

function fecharModal() {
  animandoFechamento.value = true
  setTimeout(() => {
    animandoFechamento.value = false
    emit('fechar')
  }, 250)
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: fadeSlideIn 0.3s ease-out;
}

.modal-content.modal-fechando {
  animation: fadeSlideOut 0.25s ease-in forwards;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.btn-close {
  width: 36px;
  height: 36px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  transition: background 0.2s;
}

.btn-close:hover {
  background: #e0e0e0;
}

.modal-body {
  padding: 25px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 25px;
  border-top: 1px solid #eee;
}

/* Modelo selector */
.modelo-selector {
  display: flex;
  gap: 10px;
}

.modelo-filtro {
  max-width: 180px;
  flex-shrink: 0;
}

.modelo-selector .form-input {
  flex: 1;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

/* Canal options */
.canal-options {
  display: flex;
  gap: 12px;
}

.canal-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.canal-option input {
  display: none;
}

.canal-option i {
  font-size: 24px;
  color: #666;
}

.canal-option span {
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.canal-option.selected {
  border-color: #4caf50;
  background: #f1f8e9;
}

.canal-option.selected i,
.canal-option.selected span {
  color: #388e3c;
}

/* Toggle */
.form-group label.toggle-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 0;
  font-weight: 500;
}

.toggle-input {
  display: none;
}

.toggle-slider {
  width: 44px;
  min-width: 44px;
  height: 24px;
  background: #ccc;
  border-radius: 12px;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}

.toggle-slider::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-input:checked + .toggle-slider::after {
  transform: translateX(20px);
}

.toggle-text {
  font-weight: 500;
  color: #555;
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-text i {
  color: #7b1fa2;
}

/* Editor */
.editor-wrapper {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

/* Editor Tabs */
.editor-tabs {
  display: flex;
  background: #e8e8e8;
  border-bottom: 1px solid #ddd;
}

.editor-tab {
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
}

.editor-tab:hover {
  color: #333;
  background: #f0f0f0;
}

.editor-tab.active {
  color: #4caf50;
  background: #f8f9fa;
  border-bottom-color: #4caf50;
}

/* Toolbar */
.editor-toolbar {
  background: #f8f9fa;
  padding: 8px 10px;
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.toolbar-group {
  display: flex;
  gap: 2px;
  align-items: center;
}

.toolbar-vars {
  flex-wrap: wrap;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: #d0d0d0;
  margin: 0 4px;
}

.fmt-select {
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 12px;
  color: #555;
  background: white;
  cursor: pointer;
  outline: none;
}

.fmt-select:focus {
  border-color: #4caf50;
}

.fmt-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #555;
  font-size: 13px;
  transition: all 0.15s;
}

.fmt-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.fmt-btn:active {
  background: #d0d0d0;
}

.toolbar-label {
  font-size: 12px;
  color: #666;
  margin-right: 4px;
}

.var-btn {
  padding: 4px 8px;
  background: #e3f2fd;
  color: #1976d2;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.2s;
}

.var-btn:hover {
  background: #bbdefb;
}

/* Visual editor (contenteditable) */
.editor-visual {
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.6;
  font-family: inherit;
  outline: none;
  color: #333;
  background: white;
}

.editor-visual:empty::before {
  content: 'Digite sua mensagem aqui...';
  color: #aaa;
  pointer-events: none;
}

.editor-visual :deep(a) {
  color: #1976d2;
  text-decoration: underline;
}

.editor-visual :deep(h2) {
  font-size: 22px;
  font-weight: 700;
  margin: 12px 0 8px;
}

.editor-visual :deep(h3) {
  font-size: 18px;
  font-weight: 600;
  margin: 10px 0 6px;
}

.editor-visual :deep(h4) {
  font-size: 14px;
  font-weight: 600;
  margin: 8px 0 4px;
  color: #555;
}

.editor-visual :deep(p) {
  margin: 4px 0;
}

.editor-visual :deep(ul) {
  padding-left: 24px;
  margin: 8px 0;
  list-style-type: disc;
}

.editor-visual :deep(ol) {
  padding-left: 24px;
  margin: 8px 0;
  list-style-type: decimal;
}

.editor-visual :deep(li) {
  margin: 2px 0;
  display: list-item;
}

.editor-visual :deep(b),
.editor-visual :deep(strong) {
  font-weight: 700;
}

.editor-visual :deep(i),
.editor-visual :deep(em) {
  font-style: italic;
}

.editor-visual :deep(u) {
  text-decoration: underline;
}

/* HTML textarea */
.editor-html-textarea {
  border: none;
  border-radius: 0;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #2d2d2d;
  background: #fafafa;
  min-height: 200px;
  resize: vertical;
}

/* CTA Fields */
.cta-fields {
  background: #f0f7ff;
  border: 1px solid #d0e3f7;
  border-radius: 10px;
  padding: 18px;
  margin-bottom: 20px;
}

.cta-fields .form-group {
  margin-bottom: 14px;
}

.cta-fields .form-group:last-of-type {
  margin-bottom: 10px;
}

.cta-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px dashed #c5d9ed;
}

.cta-preview-label {
  font-size: 12px;
  color: #666;
  font-weight: 600;
  flex-shrink: 0;
}

.cta-preview-btn {
  display: inline-block;
  padding: 10px 24px;
  background: #4caf50;
  color: white;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  cursor: default;
  transition: background 0.2s;
}

.cta-preview-whatsapp {
  font-size: 14px;
  color: #1976d2;
  word-break: break-all;
}

/* Buttons */
.btn-cancelar,
.btn-salvar {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-cancelar {
  background: #f5f5f5;
  color: #666;
}

.btn-cancelar:hover {
  background: #e0e0e0;
}

.btn-salvar {
  background: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-salvar:hover:not(:disabled) {
  background: #388e3c;
}

.btn-salvar:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.spinner-btn {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeSlideOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
}

/* Dark mode */
.dark-mode .modal-content {
  background: #1e1e1e;
}

.dark-mode .modal-header {
  border-color: #333;
}

.dark-mode .modal-header h2 {
  color: #f1f1f1;
}

.dark-mode .form-group label {
  color: #e0e0e0;
}

.dark-mode .form-input,
.dark-mode .form-textarea {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

.dark-mode .canal-option {
  border-color: #444;
}

.dark-mode .canal-option.selected {
  background: rgba(76, 175, 80, 0.15);
}

.dark-mode .editor-tabs {
  background: #252525;
  border-color: #444;
}

.dark-mode .editor-tab {
  color: #aaa;
}

.dark-mode .editor-tab:hover {
  color: #ddd;
  background: #333;
}

.dark-mode .editor-tab.active {
  color: #4caf50;
  background: #2a2a2a;
}

.dark-mode .editor-toolbar {
  background: #2a2a2a;
  border-color: #444;
}

.dark-mode .toolbar-divider {
  background: #555;
}

.dark-mode .fmt-select {
  background: #333;
  border-color: #555;
  color: #ccc;
}

.dark-mode .fmt-btn {
  color: #bbb;
}

.dark-mode .fmt-btn:hover {
  background: #444;
  color: #eee;
}

.dark-mode .editor-visual {
  background: #1e1e1e;
  color: #e0e0e0;
}

.dark-mode .editor-visual:empty::before {
  color: #666;
}

.dark-mode .editor-visual :deep(a) {
  color: #64b5f6;
}

.dark-mode .editor-visual :deep(h4) {
  color: #aaa;
}

.dark-mode .editor-html-textarea {
  background: #1a1a1a;
  color: #d4d4d4;
}

.dark-mode .editor-wrapper {
  border-color: #444;
}

.dark-mode .cta-fields {
  background: #1a2a3a;
  border-color: #2a3a4a;
}

.dark-mode .cta-preview {
  border-color: #2a3a4a;
}

.dark-mode .cta-preview-label {
  color: #aaa;
}

/* Medias Section */
.medias-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.medias-selecionadas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.media-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #e3f2fd;
  border-radius: 16px;
  font-size: 12px;
  color: #1565c0;
}

.media-chip i:first-child {
  font-size: 14px;
}

.media-chip-nome {
  font-weight: 600;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-chip-size {
  color: #64b5f6;
  font-size: 11px;
}

.media-chip-remove {
  background: none;
  border: none;
  color: #1565c0;
  cursor: pointer;
  padding: 0 2px;
  font-size: 12px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.media-chip-remove:hover {
  opacity: 1;
}

.btn-add-media {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f5f5f5;
  border: 1px dashed #bbb;
  border-radius: 6px;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-start;
}

.btn-add-media:hover {
  background: #e8f5e9;
  border-color: #4caf50;
  color: #388e3c;
}

/* Media Selector Modal */
.media-selector-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001;
  padding: 20px;
}

.media-selector-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.media-selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.media-selector-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.media-selector-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.media-selector-loading,
.media-selector-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: #999;
  font-size: 14px;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.media-selector-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.media-selector-item {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.media-selector-item:hover {
  border-color: #4caf50;
}

.media-selector-item.selected {
  border-color: #4caf50;
  background: #f1f8e9;
}

.media-selector-thumb {
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  overflow: hidden;
  border-radius: 4px;
  background: #f5f5f5;
}

.media-selector-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-selector-thumb i {
  font-size: 32px;
  color: #999;
}

.media-selector-thumb i.fa-file-pdf { color: #f44336; }
.media-selector-thumb i.fa-file-word { color: #1976d2; }
.media-selector-thumb i.fa-file-excel { color: #388e3c; }

.media-selector-nome {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.media-selector-size {
  display: block;
  font-size: 11px;
  color: #888;
}

.media-selector-check {
  position: absolute;
  top: 6px;
  right: 6px;
  color: #4caf50;
  font-size: 18px;
}

.media-selector-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-top: 1px solid #eee;
  font-size: 13px;
  color: #666;
}

.btn-confirmar-midias {
  padding: 8px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-confirmar-midias:hover {
  background: #43a047;
}

/* Dark mode - Medias */
.dark-mode .media-chip {
  background: #1a2a3a;
  color: #64b5f6;
}

.dark-mode .media-chip-size {
  color: #4a8abf;
}

.dark-mode .media-chip-remove {
  color: #64b5f6;
}

.dark-mode .btn-add-media {
  background: #2a2a2a;
  border-color: #555;
  color: #aaa;
}

.dark-mode .btn-add-media:hover {
  background: #1b4332;
  border-color: #4caf50;
  color: #81c784;
}

.dark-mode .media-selector-modal {
  background: #1e1e1e;
}

.dark-mode .media-selector-header {
  border-color: #333;
}

.dark-mode .media-selector-header h3 {
  color: #f1f1f1;
}

.dark-mode .media-selector-item {
  border-color: #444;
}

.dark-mode .media-selector-item.selected {
  border-color: #4caf50;
  background: rgba(76, 175, 80, 0.15);
}

.dark-mode .media-selector-thumb {
  background: #2a2a2a;
}

.dark-mode .media-selector-nome {
  color: #e0e0e0;
}

.dark-mode .media-selector-footer {
  border-color: #333;
  color: #aaa;
}

/* Inline image selector - single click to insert */
.media-inline-item {
  cursor: pointer;
  transition: all 0.2s;
}

.media-inline-item:hover {
  border-color: #4caf50;
  background: #f1f8e9;
  transform: scale(1.03);
}

.dark-mode .media-inline-item:hover {
  background: rgba(76, 175, 80, 0.15);
}

/* Inline images in editor */
.editor-visual :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 8px 0;
  display: block;
}

@media (max-width: 600px) {
  .modal-content {
    max-height: 100vh;
    border-radius: 0;
  }

  .canal-options {
    flex-direction: column;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .btn-cancelar,
  .btn-salvar {
    width: 100%;
  }

  .media-selector-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}
</style>
